import { Price, Room, RoomFormat, SelectedRoom } from 'lib/types/trip';
import { AccomodationCard } from './card';
import { AccomodationHead } from './head';
import { TDropDownItem } from 'lib/types/dropdown';
import { useCurrency } from 'lib/hooks/useCurrency';
import { Dispatch, SetStateAction } from 'react';
import { FallbackCard } from './skeleton-card';
import { useTenant } from 'lib/hooks/useTenant';

type Props = {
  rooms: Room[] | null | undefined;
  travellerDropdown: TDropDownItem | null;
  setSelectedRooms: Dispatch<SetStateAction<SelectedRoom[]>>;
  selectedRooms: SelectedRoom[];
  defaultRoom: RoomFormat[] | null;
};

const Accomodation = ({
  rooms,
  travellerDropdown,
  setSelectedRooms,
  selectedRooms,
  defaultRoom,
}: Props) => {
  const { currency } = useCurrency();
  const { isDA } = useTenant();
  const totalTravellers = travellerDropdown
    ? parseInt(travellerDropdown.value)
    : 0;
  const assignedTravellers = selectedRooms.reduce(
    (sum, item) => sum + (item?.count || 0),
    0
  );
  const isNothingAssigned = assignedTravellers === 0;

  const filterRoom = (
    roomCapacity: number,
    roomCanShare: number,
    roomIsIncluded: number,
    roomPrice: Price
  ) => {
    if (!travellerDropdown) return false;
    const roomStatus =
      roomCapacity <= parseInt(travellerDropdown?.value) ||
      (roomCanShare && roomCapacity > parseInt(travellerDropdown?.value));
    const priceStatus =
      roomIsIncluded ||
      (!roomIsIncluded &&
        roomPrice !== null &&
        JSON.stringify(roomPrice) !== '{}');
    return roomStatus && priceStatus;
  };

  const checkSelectedRoom = (roomId: number) => {
    const result = defaultRoom?.map((room) => room.id).includes(roomId)
      ? defaultRoom.filter((item) => item.id === roomId)[0].travellers_number
      : 0;
    return result;
  };

  return (
    <div className="flex flex-col gap-5">
      {isDA ? (
        <div>
          <div
            data-testid="accommodation-heading-row"
            className="flex items-center gap-2.5 min-h-[1.5rem]"
          >
            <h3 className="text-lg leading-6 font-semibold text-dark-800">
              2. Assign your travellers to rooms
            </h3>
            {isNothingAssigned && (
              <span
                data-testid="accommodation-required"
                className="shrink-0 text-xs font-bold text-dealsaway-900 bg-dealsaway-800 rounded px-2 py-1"
              >
                Required
              </span>
            )}
          </div>
          <p className="mt-2 text-sm text-dark-700 font-normal">
            Choose how many travellers will stay in each accommodation option.
            Assign everyone to continue.
          </p>
          <p
            data-testid="assigned-counter"
            className="mt-2 text-sm text-dark-800 font-semibold"
          >
            {assignedTravellers} of {totalTravellers} travellers assigned
          </p>
        </div>
      ) : (
        <h3 className="text-lg font-semibold text-dark-800">
          Select Accommodation
        </h3>
      )}
      <AccomodationHead />
      {!rooms ? (
        <>
          <FallbackCard />
          <FallbackCard />
        </>
      ) : (
        rooms
          .sort(function (a, b) {
            return a.prices[currency] - a.prices[currency];
          })
          .map((room, index) => {
            return filterRoom(
              room.capacity,
              room.can_share,
              room.is_included,
              room.prices
            ) ? (
              <AccomodationCard
                key={index}
                room={room}
                travellerDropdown={travellerDropdown}
                setSelectedRooms={setSelectedRooms}
                selectedRooms={selectedRooms}
                defaultSelected={checkSelectedRoom(room.id)}
                isRequired={isDA && isNothingAssigned}
                isFullyAssigned={
                  isDA &&
                  totalTravellers > 0 &&
                  assignedTravellers === totalTravellers
                }
              />
            ) : null;
          })
      )}
    </div>
  );
};

export { Accomodation };
